class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.querySelector('.drawer-continue-shopping')?.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const bind = (selector) => {
      const el = document.querySelector(selector);
      if (!el) return;

      // prevent duplicate bindings
      if (el.dataset.drawerBound === 'true') return;
      el.dataset.drawerBound = 'true';

      el.setAttribute('role', 'button');
      el.setAttribute('aria-haspopup', 'dialog');

      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(el);
      });

      el.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(el);
        }
      });
    };

    bind('#cart-icon-bubble');
    bind('#cart-icon-bubble-sticky');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');

    if (this.activeElement && !this.activeElement.classList.contains('quick-add__submit')) {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Parse a section HTML string and return a replacement element for the cart icon.
   * We don't assume the section contains the exact ID; we fall back and FORCE the ID.
   */
  getCartIconElementFromSection(html, wantedId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // 1) Best case: section already includes the correct id
    let el = doc.querySelector(`#${wantedId}`);

    // 2) Fallbacks: find a likely cart link/icon inside the returned section HTML
    if (!el) {
      el =
        doc.querySelector('.header__icon--cart') ||
        doc.querySelector('a[href="/cart"]') ||
        doc.querySelector('a[href^="/cart"]') ||
        doc.querySelector('a[href*="/cart"]') ||
        doc.querySelector('button[aria-label*="Cart"]') ||
        doc.querySelector('button[aria-controls*="cart"]') ||
        null;
    }

    if (!el) return null;

    // FORCE stable selectors so your CSS/JS can always find it
    el.id = wantedId;

    // Ensure cart icon has expected classes (keep any existing ones too)
    const required = ['header__icon', 'header__icon--cart', 'link', 'focus-inset'];
    required.forEach((c) => el.classList.add(c));

    return el;
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner?.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!target) return;

      // Cart icons: replace node safely (no nested anchors), force id/classes
      if (section.id === 'cart-icon-bubble' || section.id === 'cart-icon-bubble-sticky') {
        const newEl = this.getCartIconElementFromSection(parsedState.sections[section.id], section.id);
        if (newEl) {
          target.replaceWith(newEl);
        }
        return;
      }

      // Drawer body: normal innerHTML update
      target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Re-bind because icons may have been replaced
    this.setHeaderCartIconAccessibility();

   setTimeout(() => {
  this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

  // ✅ reset scroll so drawer opens at top after re-render
  const inner = this.querySelector('.drawer__inner');
  if (inner) inner.scrollTop = 0;

  this.open();
});

  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      { id: 'cart-icon-bubble-sticky', selector: '#cart-icon-bubble-sticky' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      { id: 'cart-icon-bubble-sticky', section: 'cart-icon-bubble-sticky', selector: '#cart-icon-bubble-sticky' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
